import dashboardWelcomeImg from '../../assets/placeholders/dashboard_user_guide_welcome_card.png';

// ─────────────────────────────────────────────────────────────────────────────
// dashboardBuilderTour.js — Dashboard Studio Guided Onboarding Tour
//
// Step map (post-revision):
//   1.  Welcome hero              (null target, isWelcome: true)
//   2.  Create dashboard          (#dashboard-create-btn) → waitForAction: model dialog opens
//   3.  Select data model         (#dashboard-data-model-select) → waitForAction: confirm btn enabled
//   4.  Confirm selection         (#dashboard-data-model-confirm) → waitForAction: btn gone
//   5.  Layout templates          (#widget-toolbox-templates) → Next only
//   6.  Widget toolbox            (#widget-toolbox-cards on GalleryView) → waitForAction: new widget on canvas
//   7.  Click widget to configure (.react-grid-item) → Next only (auto-selected after drag)
//   8.  Configure data fields     (#widget-config-panel) → waitForAction: field chip added
//   9.  Save                      (#dashboard-btn-save) → waitForAction: hasClickedSave
//  10.  Name your dashboard       (#dashboard-settings-confirm) → waitForAction: hasClickedSaveSettings
//         Default name pre-filled as "New Dashboard — DD/MM/YYYY HH:MM" — user can edit or accept.
//  11.  Share button              (#dashboard-btn-share) → Next only
//         Explains Share options; notes shareable link only works after publish.
//  12.  Publish                   (#dashboard-btn-publish) → waitForAction: hasClickedPublish
//  13.  Publish confirm modal     (#publish-app-confirm) → waitForAction: hasClickedPublishConfirm
//  14.  Final — Dashboard list    (#dashboard-create-btn on list page) → isWelcome: true
//         After publish, app navigates to list; modal appears there with recap.
//
// Key auto-skip fixes:
//   • Step 4 (data preview btn): REMOVED — just mentioned in step 3 content.
//   • Step 6: #widget-toolbox-cards must exist in WidgetToolbox (GalleryView); if missing,
//     TourEngine skipIfNotFound used to chain-skip steps 6–7.
//   • Step 7 (click widget): waitForAction REMOVED — widget may be auto-selected on drop,
//     making #widget-config-panel immediately visible → instant skip.
//   • Step 9 (save): uses click-tracker flag (hasClickedSave) — reliable.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns true if the DOM element exists AND has a non-zero bounding box.
 * Guards against elements that are in the DOM but hidden (height: 0 etc).
 */
const isVisible = (selector) => {
  const el = document.querySelector(selector);
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
};

// ── Click-tracking flags ──────────────────────────────────────────────────────
// Passive document listener records explicit user clicks so waitForAction
// conditions are guaranteed to be triggered by real user intent (not pre-existing DOM).
let hasClickedSave = false;
let hasClickedSaveSettings = false;
let hasClickedPublish = false;
let hasClickedPublishConfirm = false;
let hasPublishedDashboard = false;

// ── Widget drag baseline ───────────────────────────────────────────────────────
// Captured on the first poll of step 6's waitForAction.
// -1 = not yet sampled.  Comparing current count > baseline ensures we wait for
// a NEW widget to be added, even if the canvas already has widgets (e.g. template).
let widgetCountAtStep6 = -1;

if (typeof document !== 'undefined') {
  document.addEventListener('click', (e) => {
    if (e.target.closest('#dashboard-btn-save'))        hasClickedSave = true;
    if (e.target.closest('#dashboard-settings-confirm')) hasClickedSaveSettings = true;
    if (e.target.closest('#dashboard-btn-publish'))     hasClickedPublish = true;
    if (e.target.closest('#publish-app-confirm'))       hasClickedPublishConfirm = true;
  }, { passive: true });

  // Dashboard editor emits this after successful publish in DashboardsPage.
  window.addEventListener('dash:published', () => {
    hasPublishedDashboard = true;
  }, { passive: true });
}

export const getDashboardBuilderSteps = () => {
  // Reset all click-tracker flags and the widget baseline on every tour start / restart.
  hasClickedSave = false;
  hasClickedSaveSettings = false;
  hasClickedPublish = false;
  hasClickedPublishConfirm = false;
  hasPublishedDashboard = false;
  widgetCountAtStep6 = -1;

  return [

    // ─────────────────────────────────────────────────────────────────────────
    // STEP 1 — WELCOME HERO
    // ─────────────────────────────────────────────────────────────────────────
    {
      target: null,
      isWelcome: true,
      image: dashboardWelcomeImg,
      title: 'Welcome to Dashboard Studio',
      content: `Connect a Data Model, drag and drop widgets, and publish beautiful dashboards — no code required.`,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // STEP 2 — CREATE DASHBOARD
    // ─────────────────────────────────────────────────────────────────────────
    {
      target: '#dashboard-create-btn',
      title: '➕ Create Your Dashboard',
      content: `Click "CREATE DASHBOARD" to open a blank canvas.

A data model selection dialog will appear right after — this connects your dashboard to live data so your widgets can query it.

👆 Click the button now; the tour continues once the dialog opens.`,
      waitForAction: () => isVisible('#dashboard-data-model-select'),
      skipIfNotFound: true,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // STEP 3 — SELECT DATA MODEL
    // Data preview button mentioned here rather than as a separate step.
    // ─────────────────────────────────────────────────────────────────────────
    {
      target: '#dashboard-data-model-select',
      title: '🗄️ Select a Data Model',
      content: `Every dashboard needs a data source. Choose a Data Model here — it defines the tables and fields your widgets can query.

💡 Tip: Once on the canvas, click the "Data" button in the top toolbar to preview the raw records in your chosen model — handy for understanding the shape of your data before building.

👆 Click any model to select it. The Confirm button will activate once a model is highlighted.`,
      waitForAction: () => {
        const btn = document.querySelector('#dashboard-data-model-confirm');
        return !!(btn && !btn.disabled);
      },
      skipIfNotFound: true,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // STEP 4 — CONFIRM SELECTION
    // ─────────────────────────────────────────────────────────────────────────
    {
      target: '#dashboard-data-model-confirm',
      title: '✅ Confirm Selection',
      content: `Great choice! Click "Confirm Selection" to load your data model and open the Dashboard canvas.

The canvas is where you'll drag, drop, and arrange all your visualisations.`,
      waitForAction: () => !document.querySelector('#dashboard-data-model-confirm'),
      skipIfNotFound: true,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // STEP 5 — TEMPLATES
    // ─────────────────────────────────────────────────────────────────────────
    {
      target: '#widget-toolbox-templates',
      title: '🎨 Start with a Layout Template',
      content: `Don't want to build from scratch?

Click "Templates" to browse 8+ pre-built professional layouts — Executive KPI, Sales Analytics, Operations Monitor, and more.

Each template instantly populates your canvas with a complete widget arrangement. You can customise everything after applying.`,
      skipIfNotFound: true,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // STEP 6 — WIDGET TOOLBOX (mandatory drag)
    // Smart waitForAction:
    //   • Captures the baseline widget count the first time it evaluates.
    //   • If widgets already exist (e.g. user applied a template in step 5),
    //     the baseline is > 0 → advances immediately (correct — user already added).
    //   • If the canvas is empty, waits until the user drags at least one widget.
    // ─────────────────────────────────────────────────────────────────────────
    {
      target: '#widget-toolbox-cards',
      title: '📊 Add Visualisation Widgets',
      content: `This is your Widget Toolbox — everything you can put on the canvas:

📈 KPI Card — single value metric with trend indicator
📊 Bar / Horizontal Bar — compare categories
📉 Line / Area — trends over time
🍩 Donut / Pie — composition & shares
📋 Table — tabular drill-down data
🎛️ Gauge — progress towards a target
🔀 Filter widget — let viewers slice the data themselves

👆 Drag any widget card from this panel and drop it onto the canvas.
The tour will automatically continue once a widget is placed.`,
      waitForAction: () => {
        const current = document.querySelectorAll('.react-grid-item').length;
        // Capture baseline on first evaluation
        if (widgetCountAtStep6 === -1) widgetCountAtStep6 = current;
        // If canvas already had widgets when step started → skip immediately
        if (widgetCountAtStep6 > 0) return true;
        // Otherwise wait for the user to add at least one new widget
        return current > widgetCountAtStep6;
      },
      skipIfNotFound: true,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // STEP 7 — CLICK TO SELECT A WIDGET
    // waitForAction REMOVED:
    //   After drag-drop, the widget is auto-selected; DataConfigView mounts with
    //   id="widget-config-panel". waitForAction uses [data-tour="field-chip"] on zone chips.
    //   Now a plain spotlight with Next.
    // ─────────────────────────────────────────────────────────────────────────
    {
      target: '.react-grid-item',
      title: '👆 Click the Widget to Configure It',
      content: `Click on the widget you placed on the canvas.

This opens the configuration panel in the right sidebar where you'll:
• Connect your data model fields to the chart axes
• Set filters and aggregations
• Switch to the "Format" tab to control colours, fonts, and sizing

👆 Click your widget on the canvas, then click Next.`,
      skipIfNotFound: true,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // STEP 8 — CONFIGURE DATA FIELDS
    // ─────────────────────────────────────────────────────────────────────────
    {
      target: '#widget-config-panel',
      title: '⚙️ Configure Your Data Fields',
      content: `This is the Data Configuration Panel.

• Drag fields from your data model into the drop zones (X-Axis, Y-Axis, Value, etc.)
• The widget updates live as you assign each field
• Switch to the "Format" tab at any time to customise colours, labels, and chart style

👆 Drag at least one field into a drop zone — the tour advances automatically once a field is assigned.`,
      waitForAction: () => document.querySelectorAll('[data-tour="field-chip"]').length > 0,
      skipIfNotFound: true,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // STEP 9 — SAVE
    // ─────────────────────────────────────────────────────────────────────────
    {
      target: '#dashboard-btn-save',
      title: '💾 Save Your Dashboard',
      content: `Save your progress to create a Draft version. Drafts are private to you until you Publish.

💡 The yellow dot on the Save button signals there are unsaved changes. Save frequently as you build!

👆 Click Save now — a naming dialog will appear so you can give your dashboard a proper title.`,
      waitForAction: () => hasClickedSave,
      skipIfNotFound: true,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // STEP 10 — NAME YOUR DASHBOARD
    // The name field is now pre-filled with "New Dashboard — DD/MM/YYYY HH:MM"
    // so the Confirm & Save button is immediately active.
    // User can edit the name or just click through.
    // ─────────────────────────────────────────────────────────────────────────
    {
      target: '#dashboard-settings-confirm',
      title: '📝 Name Your Dashboard',
      content: `A timestamped default name has been pre-filled for you — feel free to replace it with something meaningful like "Q2 Sales Overview" or "Patient Appointment Summary".

You can also:
• Add a description (shown in the version picker)
• Enable the Shareable Link toggle (you can do this later too)
• Choose a visual Theme

👆 Edit the name if you wish, then click "Confirm & Save" to save your dashboard.`,
      waitForAction: () => hasClickedSaveSettings,
      // Safely skips if modal doesn't open (dashboard already has a name)
      skipIfNotFound: true,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // STEP 11 — SHARE BUTTON
    // Appears after save because Share button only renders once a dashboardId exists.
    // Explains Share menu; critical note: shareable link is inactive until published.
    // ─────────────────────────────────────────────────────────────────────────
    {
      target: '#dashboard-btn-share',
      title: '🔗 The Share Menu',
      content: `After saving, the Share button appears in the top-right toolbar. It gives you several export and sharing options:

📄 Export as PDF — snapshot or full backend report
📊 Export as CSV / Excel (ZIP) — all widget data
🔗 Embed / Publish URL — a live public link

⚠️ Important: The "Shareable Link" option only becomes active after you Publish your dashboard. While it's still a Draft, the link will return a "not found" error.

Click Next to proceed to the final step: Publish.`,
      // Required final step: do not auto-skip during post-save toolbar re-render.
      // If it renders a bit late, TourEngine will keep waiting instead of ending.
      skipIfNotFound: false,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // STEP 12 — PUBLISH (TOOLBAR BUTTON)
    // ─────────────────────────────────────────────────────────────────────────
    {
      target: '#dashboard-btn-publish',
      title: '🚀 Publish',
      content: `Now publish your dashboard to make it live.

Step 1: Click Publish in the toolbar to open the publish popup.`,
      waitForAction: () => hasClickedPublish,
      skipIfNotFound: false,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // STEP 13 — PUBLISH (POPUP CONFIRM BUTTON) — FINAL ACTION STEP
    // ─────────────────────────────────────────────────────────────────────────
    {
      target: '#publish-app-confirm',
      title: '✅ Confirm Publish (Final Action)',
      content: `Step 2: Click Publish in this popup to confirm.

The tour will finish only after the popup publish is confirmed.`,
      // Must wait for the modal-confirm action (and modal close), or a
      // definitive successful publish signal from the page.
      waitForAction: () => {
        const confirmStillVisible = !!document.querySelector('#publish-app-confirm');
        return (hasClickedPublishConfirm && !confirmStillVisible) || hasPublishedDashboard;
      },
      skipIfNotFound: false,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // STEP 14 — FINAL MESSAGE
    // ─────────────────────────────────────────────────────────────────────────
    {
      target: null,
      title: '🎉 Dashboard Published!',
      content: `Your dashboard is now live and visible in the sidebar/dashboard list.

You can share it with users based on your access settings.`,
    },

  ];
};
